import type { Format, Season } from "@pra/shared/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Empty, ErrorBox, PageHead } from "../components/Bits";
import { Badge, Button, Card, Input, Select, Table } from "../design-system/components";
import { endpoints } from "../lib/api";
import { formatDate } from "../lib/format";
import { useSession } from "../lib/session";

function SeasonEditor({
  season,
  formats,
  onDone,
}: {
  season: Season;
  formats: Format[];
  onDone: () => void;
}) {
  const client = useQueryClient();
  const [name, setName] = useState(season.name);
  const [formatId, setFormatId] = useState(season.formatId ?? "");
  const [startsAt, setStartsAt] = useState(season.startsAt ?? "");
  const [endsAt, setEndsAt] = useState(season.endsAt ?? "");
  const [notes, setNotes] = useState(season.notes ?? "");
  const save = useMutation({
    mutationFn: () =>
      endpoints.patchSeason(season.seasonId, {
        name,
        formatId: formatId || null,
        startsAt: startsAt || null,
        endsAt: endsAt || null,
        notes: notes || null,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["seasons"] });
      onDone();
    },
  });
  return (
    <div
      className="pr-form"
      style={{
        padding: "12px 16px",
        background: "var(--surface-well)",
        borderRadius: "var(--radius)",
      }}
    >
      <div className="rk-split">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Select
          label="Format"
          value={formatId}
          onChange={setFormatId}
          placeholder="No format"
          options={[
            { value: "", label: "No format" },
            ...formats.map((f) => ({ value: f.formatId, label: f.name })),
          ]}
        />
      </div>
      <div className="rk-split">
        <Input
          label="Starts"
          type="date"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
        />
        <Input
          label="Ends"
          type="date"
          value={endsAt}
          onChange={(e) => setEndsAt(e.target.value)}
        />
      </div>
      <Input
        as="textarea"
        label="Notes"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      {save.error && <ErrorBox error={save.error} />}
      <div className="pr-inline">
        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={save.isPending || name.trim().length === 0}
        >
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function FormatEditor({ format, onDone }: { format?: Format; onDone: () => void }) {
  const client = useQueryClient();
  const [name, setName] = useState(format?.name ?? "");
  const [description, setDescription] = useState(format?.description ?? "");
  const [startsAt, setStartsAt] = useState(format?.startsAt ?? "");
  const [endsAt, setEndsAt] = useState(format?.endsAt ?? "");
  const save = useMutation({
    mutationFn: () => {
      const body = { name, description, startsAt, endsAt };
      return format ? endpoints.updateFormat(format.formatId, body) : endpoints.createFormat(body);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["seasons"] });
      onDone();
    },
  });
  return (
    <div
      className="pr-form"
      style={{
        padding: "12px 16px",
        background: "var(--surface-well)",
        borderRadius: "var(--radius)",
      }}
    >
      <Input
        label="Name"
        placeholder="Standard (G–I)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Input
        label="Description"
        placeholder="Legal sets / regulation marks"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="rk-split">
        <Input
          label="From"
          type="date"
          value={startsAt}
          onChange={(e) => setStartsAt(e.target.value)}
        />
        <Input label="To" type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
      </div>
      {save.error && <ErrorBox error={save.error} />}
      <div className="pr-inline">
        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={save.isPending || name.trim().length === 0}
        >
          {format ? "Save" : "Add format"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function SeasonsPage() {
  const { isAdmin } = useSession();
  const client = useQueryClient();
  const data = useQuery({ queryKey: ["seasons"], queryFn: endpoints.seasons });
  const [editingSeason, setEditingSeason] = useState<string | undefined>(undefined);
  const [editingFormat, setEditingFormat] = useState<string | "new" | undefined>(undefined);
  const remove = useMutation({
    mutationFn: (id: string) => endpoints.deleteFormat(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["seasons"] }),
  });
  if (data.error) return <ErrorBox error={data.error} />;
  const seasons = data.data?.seasons ?? [];
  const formats = data.data?.formats ?? [];
  const range = (a?: string, b?: string) =>
    a || b ? `${a ? formatDate(a) : "…"} – ${b ? formatDate(b) : "…"}` : "—";
  return (
    <div className="rk-stack">
      <PageHead
        title="Seasons & formats"
        subtitle="Seasons are created the first time a log mentions them; the id and title come from TCG Live, everything else is maintained here."
      />
      <Card title="Seasons" flush>
        {seasons.length === 0 ? (
          <Empty title="No seasons yet">
            <p>The first log with a [CompetitiveElo] line creates the season.</p>
          </Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Id</th>
                <th>Season</th>
                <th>Format</th>
                <th>Dates</th>
                <th>Notes</th>
                <th>First seen</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {seasons.map((s) => (
                <tr key={s.seasonId}>
                  <td className="num">{s.seasonId}</td>
                  <td>
                    {editingSeason === s.seasonId ? (
                      <SeasonEditor
                        season={s}
                        formats={formats}
                        onDone={() => setEditingSeason(undefined)}
                      />
                    ) : (
                      s.name
                    )}
                  </td>
                  <td>{s.format ?? <span className="pr-muted">—</span>}</td>
                  <td className="pr-muted">{range(s.startsAt, s.endsAt)}</td>
                  <td className="pr-muted">{s.notes ?? ""}</td>
                  <td className="pr-muted">{formatDate(s.firstSeenAt)}</td>
                  {isAdmin && (
                    <td>
                      {editingSeason !== s.seasonId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingSeason(s.seasonId)}
                        >
                          Edit
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Card
        title="Standard formats"
        description="Which format was legal when."
        flush
        actions={
          isAdmin && editingFormat === undefined ? (
            <Button size="sm" onClick={() => setEditingFormat("new")}>
              Add format
            </Button>
          ) : undefined
        }
      >
        {editingFormat === "new" && (
          <div style={{ padding: 16 }}>
            <FormatEditor onDone={() => setEditingFormat(undefined)} />
          </div>
        )}
        {formats.length === 0 && editingFormat !== "new" ? (
          <Empty title="No formats yet">
            {isAdmin ? (
              <p>Add the first one with the button above.</p>
            ) : (
              <p>An admin hasn't added any yet.</p>
            )}
          </Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Format</th>
                <th>Description</th>
                <th>Dates</th>
                <th>Seasons</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {formats.map((f) => (
                <tr key={f.formatId}>
                  <td>
                    {editingFormat === f.formatId ? (
                      <FormatEditor format={f} onDone={() => setEditingFormat(undefined)} />
                    ) : (
                      f.name
                    )}
                  </td>
                  <td className="pr-muted">{f.description ?? ""}</td>
                  <td className="pr-muted">{range(f.startsAt, f.endsAt)}</td>
                  <td>
                    {seasons
                      .filter((s) => s.formatId === f.formatId)
                      .map((s) => (
                        <Badge key={s.seasonId} variant="outline" style={{ marginRight: 4 }}>
                          {s.name}
                        </Badge>
                      ))}
                  </td>
                  {isAdmin && (
                    <td className="pr-inline">
                      {editingFormat !== f.formatId && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingFormat(f.formatId)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => remove.mutate(f.formatId)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
